'use client';

/**
 * DocsPanel - Documentation Q&A Response Display
 *
 * Displays the docs response in the Copilot panel.
 * Shows short/long answer, sources, and follow-up suggestions.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain docs
 */

import { BookOpen, FileText, Lightbulb, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useDocsStore } from '@/lib/stores/docs-store';
import { cn } from '@/lib/utils/cn';

export function DocsPanel() {
  const { response, isLoading } = useDocsStore();
  const [showLongAnswer, setShowLongAnswer] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <Loader2 className="h-8 w-8 mb-2 animate-spin text-primary" />
        <p className="text-sm">Searching documentation...</p>
      </div>
    );
  }

  if (!response) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <BookOpen className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">Documentation Q&A</p>
        <p className="text-xs mt-1">Ask a question in the Explorer panel</p>
      </div>
    );
  }

  const confidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Header with confidence badge */}
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Answer</span>
        </div>
        <span className={cn('text-xs px-2 py-0.5 rounded-full', confidenceColor(response.confidence))}>
          {response.confidence}
        </span>
      </div>

      {/* Short Answer */}
      <div className="p-3 border-b border-border">
        <p className="text-sm font-medium">{response.short_answer}</p>
      </div>

      {/* Long Answer (collapsible) */}
      <div className="border-b border-border">
        <button
          onClick={() => setShowLongAnswer(!showLongAnswer)}
          className="w-full p-3 flex items-center justify-between hover:bg-accent/50 transition-colors"
        >
          <span className="text-xs font-medium text-muted-foreground">Detailed Answer</span>
          {showLongAnswer ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {showLongAnswer && (
          <div className="px-3 pb-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {response.long_answer}
            </p>
          </div>
        )}
      </div>

      {/* Sources */}
      {response.sources.length > 0 && (
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Sources ({response.sources.length})</span>
          </div>
          <div className="space-y-1">
            {response.sources.map((source, i) => (
              <div
                key={i}
                className="text-xs p-2 rounded bg-accent/30 hover:bg-accent/50 transition-colors"
              >
                <div className="font-medium truncate">{source.title}</div>
                <div className="text-muted-foreground truncate">{source.section}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Follow-up Suggestions */}
      {response.followup_suggestions.length > 0 && (
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Follow-up Questions</span>
          </div>
          <div className="space-y-1">
            {response.followup_suggestions.map((suggestion, i) => (
              <div
                key={i}
                className="text-xs p-2 rounded bg-accent/30 text-muted-foreground"
              >
                {suggestion}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Token usage footer */}
      <div className="mt-auto p-2 border-t border-border text-xs text-muted-foreground flex justify-between">
        <span>Turn {response.turn}</span>
        <span>{response.usage.total_tokens.toLocaleString()} tokens</span>
        <span>{(response.total_ms / 1000).toFixed(1)}s</span>
      </div>
    </div>
  );
}
